import type { MetricReport } from "@/types";

const metricPresentation = {
  translation: {
    color: "#eab308",
    subtitle: "Have all system requirements been broken down?",
    description: "System requirements must have driven requirements or tests directly linked.",
  },
  incorporation: {
    color: "#a855f7",
    subtitle: "How well do we understand the project?",
    description: "Every testable leaf requirement must have at least one linked test.",
  },
  completion: {
    color: "#3b82f6",
    subtitle: "How far along is the project?",
    description: "A requirement counts only when every linked test is complete.",
  },
  automation: {
    color: "#22c55e",
    subtitle: "How sustainable is our verification?",
    description: "A requirement counts only when every linked test is automated or pipeline based.",
  },
} as const;

function ReportDonut({ ratio, color, target }: { ratio: number; color: string; target: number | null }) {
  const circumference = 2 * Math.PI * 42;
  const boundedRatio = Math.max(0, Math.min(ratio, 1));
  const boundedTarget = target == null ? null : Math.max(0, Math.min(target, 1));
  const targetAngle = boundedTarget == null ? 0 : boundedTarget * Math.PI * 2 - Math.PI / 2;
  return (
    <svg viewBox="0 0 100 100" className="h-28 w-28" role="img" aria-label={`${Math.round(ratio * 100)} percent${target == null ? "" : `, target ${Math.round(target * 100)} percent`}`}>
      <circle cx="50" cy="50" r="42" fill="none" stroke="#e2e8f0" strokeWidth="12" />
      <circle
        cx="50"
        cy="50"
        r="42"
        fill="none"
        stroke={color}
        strokeWidth="12"
        strokeLinecap="round"
        strokeDasharray={`${circumference * boundedRatio} ${circumference}`}
        transform="rotate(-90 50 50)"
      />
      {boundedTarget != null && (
        <line
          x1={50 + Math.cos(targetAngle) * 34}
          y1={50 + Math.sin(targetAngle) * 34}
          x2={50 + Math.cos(targetAngle) * 50}
          y2={50 + Math.sin(targetAngle) * 50}
          stroke={boundedRatio >= boundedTarget ? "#166534" : "#7f1d1d"}
          strokeWidth="3"
          strokeLinecap="round"
          data-target-marker="true"
        />
      )}
      <text x="50" y="55" textAnchor="middle" fill={color} fontSize="18" fontWeight="700">{Math.round(ratio * 100)}%</text>
    </svg>
  );
}

export function MetricsReportContent({ report, includeDisciplines }: { report: MetricReport; includeDisciplines: boolean }) {
  const formatTimestamp = (value: string) => new Intl.DateTimeFormat(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: report.timezone,
    timeZoneName: "short",
  }).format(new Date(value));
  const effectiveDate = report.mode === "LIVE"
    ? `Live snapshot: ${formatTimestamp(report.captured_at)}`
    : `Report date: ${report.report_date} (end of day, ${report.timezone})`;

  return (
    <article className="metrics-report-content bg-white text-slate-900 rounded-[10px] p-6" data-testid="metrics-report-content">
      <header className="mb-5 border-b border-slate-300 pb-3">
        <h2 className="m-0 text-xl">{report.project_name}</h2>
        <div className="mt-1 text-sm">Project status: {report.project_status || "Not set"}</div>
        <div className="mt-1 text-xs text-slate-700" data-testid="metrics-report-releases">
          {report.release_versions.length ? report.release_versions.map(release => (
            <div key={release.sku_name}>
              {release.sku_name} release: {release.version || "Not published"} · Last updated: {release.updated_on || "Not available"}
            </div>
          )) : "Release: Not available"}
        </div>
        <div className="mt-1 text-xs text-slate-600">{effectiveDate}</div>
      </header>
      <section className="grid grid-cols-[repeat(auto-fit,minmax(180px,1fr))] gap-3">
        {report.metrics.map(metric => {
          const presentation = metricPresentation[metric.key];
          return (
            <div key={metric.key} className="border border-slate-300 rounded-lg p-4 break-inside-avoid">
              <h3 className="m-0 text-sm">{metric.label}</h3>
              <p className="mt-1 min-h-8 text-xs text-slate-600">{presentation.subtitle}</p>
              <div className="my-2 flex justify-center"><ReportDonut ratio={metric.ratio} color={presentation.color} target={metric.target ?? null} /></div>
              <div className="text-xs font-semibold text-slate-700">{metric.actual} / {metric.total}{metric.target == null ? "" : ` · Target ${Math.round(metric.target * 100)}%`}</div>
              <p className="mb-0 mt-2 text-xs leading-relaxed text-slate-600">{presentation.description}</p>
            </div>
          );
        })}
      </section>
      {includeDisciplines && report.disciplines.length > 0 && (
        <section className="mt-5">
          <h3 className="text-sm">Discipline breakdown</h3>
          <table className="w-full border-collapse text-xs">
            <thead><tr><th className="border-b p-2 text-left">Discipline</th><th className="border-b p-2 text-left">Metrics</th></tr></thead>
            <tbody>{report.disciplines.map(row => <tr key={row.discipline}><td className="border-b p-2">{row.discipline}</td><td className="border-b p-2">{row.metrics.map(metric => `${metric.label}: ${Math.round(metric.ratio * 100)}%`).join(" · ")}</td></tr>)}</tbody>
          </table>
        </section>
      )}
      <section className="mt-5 border-t border-slate-300 pt-4">
        <h3 className="m-0 text-sm">About these metrics</h3>
        <p className="mb-0 mt-2 text-xs leading-relaxed text-slate-600">Translation, incorporation, completion and automation track requirements from breakdown through sustainable verification.</p>
      </section>
      <footer className="mt-5 text-[10px] text-slate-500">Report ID: {report.id} · Generated {formatTimestamp(report.generated_at)}</footer>
    </article>
  );
}
